import React, {
  memo, useCallback, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { actionCreators } from 'react/api/actions/integrations';
import { getResourceTo } from 'react/utils/utils';

import SwitchCheckbox from 'react/components/Integrations/SwitchCheckbox/SwitchCheckbox';
import IntegrationModal from 'react/containers/modals/IntegrationModal';
import IntegrationCard from './IntegrationCard';

function RoutePlannerIntegrationCard({
  id,
  integrationData,
  active,
  createdStatically
}) {
  const dispatch = useDispatch();

  const [isIntegrationModalOpen, setIntegrationModalOpen] = useState(false);

  const {
    integration,
    lastErrors,
    enabled
  } = useMemo(
    () => JSON.parse(integrationData),
    [integrationData]
  );

  const handleModalConnectIntegration = () => {
    setIntegrationModalOpen(!isIntegrationModalOpen);
  };

  const closeModalAction = useCallback(() => {
    handleModalConnectIntegration();

    dispatch(actionCreators.clearIntegrationConnectState());
  }, [handleModalConnectIntegration]);

  const confirmModalAction = useCallback((data) => {
    const tenant = {
      name: Greenmile.Utils.getTenant(),
      id: Monet.Session.Configurations.authConfigId
    };
    const { name, authType } = integration;
    const {
      url, username, password, type, companyName
    } = data;

    const payload = {
      id,
      integration: {
        name,
        type,
        authType
      },
      url,
      credentials: {
        username,
        password
      },
      tenant,
      enabled,
      retryAttempts: 2,
      retryBackoff: 5000,
      createdStatically
    };

    if (type === 'LNOS') {
      payload.credentials.companyName = companyName;
    }

    dispatch(actionCreators.connectIntegration(payload));
  }, [integration]);

  const handleOnChangeSwitch = useCallback(({ checked }) => {
    if (checked) {
      handleModalConnectIntegration();
    }
  }, []);

  const confirmSwitchDisableCallback = useCallback(() => {
    const payload = {
      id
    };

    dispatch(actionCreators.disableIntegration(payload));
  }, [id]);

  return (
    <IntegrationCard
      id={id}
      isConfigurable
      hasError={!!lastErrors}
      cardName={getResourceTo('IntegrationModal.ConnectIntegration.Title', integration.name)}
      active={active}
      onClickOnConfig={handleModalConnectIntegration}
    >
      <div className="IntegrationSlide">
        {isIntegrationModalOpen && (
          <IntegrationModal
            id={id}
            isOpen={isIntegrationModalOpen}
            i18n={Monet.I18n}
            title={getResourceTo('IntegrationModal.ConnectIntegration.Title', integration.name)}
            message={getResourceTo('IntegrationModal.ConnectIntegration.Message', integration.name)}
            integrationData={integrationData}
            confirmActionCallback={confirmModalAction}
            cancelActionCallback={closeModalAction}
          />
        )}
        <SwitchCheckbox
          checked={active}
          onChange={handleOnChangeSwitch}
          onDisableCallback={confirmSwitchDisableCallback}
          integrationData={integrationData}
        />
      </div>
      { active && (
        <div data-testid={`integration-description-${id}`} className="IntegrationDescription">
          {lastErrors > 0 ? (
            <span data-testid={`errors-in-last-24-${id}`} className="ErrorMessage">
              {getResourceTo('IntegrationCard.RequestsFaileds', lastErrors)}
            </span>
          ) : (
            <span data-testid={`no-errors-in-last-24-${id}`}>
              {getResourceTo('IntegrationCard.NoRequestErrors')}
            </span>
          )}
        </div>
      )}
      {!createdStatically && active && (
        <div data-testid={`view-logs-${id}`} className="ViewLogs">
          <Link data-testid={`view-logs-link-${id}`} to={`/Integrations/logs/${id}`}>
            <span>{getResourceTo('IntegrationCard.ViewLogsButton')}</span>
          </Link>
        </div>
      )}
    </IntegrationCard>
  );
}

RoutePlannerIntegrationCard.propTypes = {
  id: PropTypes.string,
  integrationData: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  createdStatically: PropTypes.bool.isRequired
};

RoutePlannerIntegrationCard.defaultProps = {
  id: null
};

export default memo(RoutePlannerIntegrationCard);
